import { z } from 'zod';
import { PageMetaSchema, PageQuerySchema, SortDirectionSchema } from './pagination.js';
/**
 * Append-only privileged audit events.
 *
 * Records are written in the same database transaction as the privileged
 * write they describe (fail closed), and the table forbids UPDATE/DELETE via
 * trigger. Values here are ALREADY redacted — secrets and prohibited data are
 * stripped before persistence.
 */
export const AUDIT_ACTIONS = [
    'auth.login',
    'auth.login.denied',
    'auth.logout',
    'access.denied',
    'overview.read',
    'organisation.create',
    'organisation.update',
    'organisation.archive',
    'organisation.lifecycle.transition',
    'organisation.read',
    'organisation.list',
    'audit.read',
    'integration.webhook.accepted',
    'integration.webhook.rejected',
    // Tenant signups — a hospital asking to become a customer. 'signup.received'
    // is written by the webhook path, the other two by an operator's decision.
    'signup.received',
    'signup.approve',
    'signup.reject',
    'integration.outbox.dispatched',
    // Stage 2 — onboarding
    'onboarding.instance.start',
    'onboarding.step.update',
    'onboarding.evidence.add',
    'onboarding.waiver.grant',
    'onboarding.golive',
    // Stage 2 — workspace
    'note.create',
    'note.archive',
    'note.read.security',
    'task.create',
    'task.update',
    'milestone.update',
    'commitment.update',
    // Stage 2 — support
    'case.create',
    'case.update',
    'case.attachment.add',
    'case.attachment.rejected',
    // Stage 2 — approvals
    'approval.submit',
    'approval.approve',
    'approval.reject',
    'approval.execute',
    'approval.expire',
    'approval.cancel',
    // Stage 2 — notifications and data movement
    'notification.delivery.dead',
    // Stage 5
    'health.review',
    'compliance.control.update',
    'compliance.evidence.add',
    'compliance.control.test',
    'compliance.exception.grant',
    'compliance.evidence.export',
    'risk.update',
    'incident.update',
    'vendor.update',
    'dpia.update',
    'automation.rule.update',
    'automation.killswitch',
    'automation.run',
    'report.generate',
    'import.execute',
    'export.execute',
    // Stage 3 — commercial, entitlements, billing, provisioning
    'contract.create',
    'contract.amend',
    'contract.terminate',
    'entitlement.override',
    'entitlement.drift.reconcile',
    'billing.invoice.create',
    'billing.invoice.settle',
    'billing.invoice.void',
    'provisioning.command.queue',
    'provisioning.command.ack',
    'provisioning.command.fail',
    // Stage 4 — fleet, telemetry, releases, reliability
    'fleet.installation.register',
    'fleet.installation.update',
    'fleet.key.rotate',
    'fleet.key.revoke',
    'release.create',
    'release.publish',
    'release.campaign.create',
    'release.campaign.start',
    'release.campaign.pause',
    'release.campaign.resume',
    'release.campaign.rollback',
    'reliability.probe.create',
    'reliability.anomaly.resolve',
    // Operator provisioning. These are the only actions in the catalogue that
    // originate outside an authenticated session: they come from the
    // break-glass CLI that bootstraps the first operator into an empty
    // production database, before anyone can sign in to grant access through
    // the product. They carry a named human in actor_display_name and a
    // mandatory reason instead of an actor_user_id.
    'operator.provision',
    'operator.role.grant',
    'operator.role.revoke',
    'operator.deactivate',
    'operator.reactivate',
];
export const AuditActionSchema = z.enum(AUDIT_ACTIONS);
export const AuditEventSchema = z.object({
    id: z.uuid(),
    occurredAt: z.iso.datetime(),
    actorUserId: z.uuid().nullable(),
    actorDisplayName: z.string().nullable(),
    actorRoles: z.array(z.string()),
    action: AuditActionSchema,
    objectType: z.string().nullable(),
    objectId: z.string().nullable(),
    objectLabel: z.string().nullable(),
    /** Operator-supplied reason or request context. */
    reason: z.string().nullable(),
    /** Redacted safe snapshot before the change (writes only). */
    before: z.unknown().nullable(),
    /** Redacted safe snapshot after the change (writes only). */
    after: z.unknown().nullable(),
    correlationId: z.string(),
    sourceIp: z.string().nullable(),
    outcome: z.enum(['success', 'denied', 'rejected']),
});
export const AuditListQuerySchema = PageQuerySchema.extend({
    action: AuditActionSchema.optional(),
    actorUserId: z.uuid().optional(),
    objectType: z.string().max(100).optional(),
    objectId: z.string().max(100).optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    sortDir: SortDirectionSchema.default('desc'),
});
export const AuditListResponseSchema = z.object({
    items: z.array(AuditEventSchema),
    meta: PageMetaSchema,
});
//# sourceMappingURL=audit.js.map